import { useState, useEffect, useRef } from 'react';

import { LazyLoadImage } from 'react-lazy-load-image-component';
import SignatureCanvas from 'react-signature-canvas';
import { Formik, Form, useFormikContext } from 'formik';
import * as yup from 'yup';
import { v4 as uuid_v4 } from 'uuid';

import { api, useAPIContext } from '../../../api/context';
import dialogs from '../../../dialogs';
import toast from '../../../utils/toast';
import html from '../../../components/html';
import addr from '../../../components/addr';
import Base from '../../Base';
import styles from './OptInOut.module.scss';

const ActionsSection = ({ loading, optOut, requiresAddress, addressVerified, verifyAddress }) => {
  const { values } = useFormikContext();

  const handleVerifyClick = () => {
    verifyAddress(values);
  };

  // If address is required and not yet verified, show Verify Address button
  const showVerifyButton = requiresAddress && !addressVerified;

  return (
    <div className={styles.actions}>
      {showVerifyButton ? (
        <button
          aria-label='verify address'
          type='button'
          onClick={handleVerifyClick}
          disabled={loading}>
          Verify Address
        </button>
      ) : (
        <>
          {requiresAddress && addressVerified && (
            <p className={styles.confirmMessage}>Your address has been updated. Please confirm.</p>
          )}
          <button
            aria-label='accept'
            type='submit'
            disabled={loading}>
            {requiresAddress ? 'Confirm Address and Accept Terms' : 'Accept'}
          </button>
        </>
      )}

      <p>
        If this campaign is not right for you, you can opt-out
        of this one and wait for the next one.
      </p>

      <button
        className={styles.alt}
        aria-label='opt out'
        type='button'
        onClick={() => optOut()}
        disabled={loading}>
        Opt-Out of This Campaign
      </button>

      <p>
        Don't worry, this won't affect your participation
        in future campaigns.
      </p>
    </div>
  );
};

const VarietyDropdown = ({ index, selectedOption, availableOptions, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionId) => {
    onSelect(optionId);
    setIsOpen(false);
  };

  return (
    <div className={styles.dropdownRow} ref={dropdownRef}>
      <span className={styles.dropdownLabel}>Variety {index + 1}:</span>
      <div className={styles.customDropdown}>
        <div
          className={`${styles.dropdownTrigger} ${isOpen ? styles.open : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          role='button'
          aria-expanded={isOpen}
        >
          {selectedOption ? (
            <>
              <img
                src={selectedOption.image}
                alt={selectedOption.label}
                className={styles.triggerImage}
              />
              <span className={styles.triggerLabel}>{selectedOption.label}</span>
            </>
          ) : (
            <span className={styles.placeholder}>-- Select --</span>
          )}
          <span className={styles.arrow}>{isOpen ? '▲' : '▼'}</span>
        </div>

        {isOpen && (
          <div className={styles.dropdownMenu}>
            {selectedOption && (
              <div
                className={styles.dropdownItem}
                onClick={() => handleSelect('')}
              >
                <span className={styles.clearOption}>Clear selection</span>
              </div>
            )}
            {availableOptions.map(option => (
              <div
                key={option.id}
                className={`${styles.dropdownItem} ${option.id === selectedOption?.id ? styles.selected : ''}`}
                onClick={() => handleSelect(option.id)}
              >
                <img
                  src={option.image}
                  alt={option.label}
                  className={styles.optionImage}
                />
                <span className={styles.optionLabel}>{option.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const OptInOut = ({ campaign }) => {

  const { token } = useAPIContext();

  /*
   * initials
   */

  const [ initials, setInitials ] = useState();

  useEffect(() => {

    api(token).v1.profile.query()
      .then(res => {
        const data = res.body.data;
        setInitials(`${data.first_name[0]}${data.last_name[0]}`.toLowerCase());
      })
      .catch(err => {
        setInitials(null);
        toast.show(toast.KIND.WARNING, err);
      });

  }, [token]);

  /*
   * data
   */

  const [ loading, setLoading ] = useState(true);
  const [ data, setData ] = useState();

  useEffect(() => {

    api(token).v1.campaigns
      .obtain({ campaign: campaign.id })
      .then(res => {
        setLoading(false);
        setData(res.body.data);
      })
      .catch(err => {
        setLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });

  }, [token, campaign]);

  /*
   * terms
   */

  const [ termsAccepted, setTermsAccepted ] = useState(false);

  /*
   * boxes
   */

  const boxSelectionMin = Math.max(campaign.box_selection_min ?? 1, 1);
  const boxSelectionMax = Math.max(campaign.box_selection_max ?? boxSelectionMin, boxSelectionMin);
  const isMultiBox = boxSelectionMax > 1;

  const [ boxIds, setBoxIds ] = useState(() => {
    if (campaign.boxes.length === 1) {
      return [campaign.boxes[0].id];
    } else {
      return [];
    }
  });

  // For single-box mode, convenience accessor
  const boxId = boxIds[0] || null;

  const handleBoxToggle = (id) => {
    if (isMultiBox) {
      setBoxIds(prev => {
        if (prev.includes(id)) {
          // Remove and clear its varieties
          const newVarieties = { ...varietySelections };
          delete newVarieties[id];
          setVarietySelections(newVarieties);
          return prev.filter(bid => bid !== id);
        }
        if (prev.length < boxSelectionMax) {
          return [...prev, id];
        }
        return prev;
      });
    } else {
      setBoxIds([id]);
      setVarietySelections({});
    }
  };

  /*
   * varieties
   *
   * For multi-box: { boxId: [varietyIds] }
   * For single-box: { boxId: [varietyIds] } (same structure, single key)
   */

  const [ varietySelections, setVarietySelections ] = useState({});

  // Get selected boxes' variety info
  const selectedBoxes = campaign.boxes.filter(box => boxIds.includes(box.id));
  const boxesWithVarieties = selectedBoxes.filter(box => box.variety_selection_count > 0);
  const requiresVariety = boxesWithVarieties.length > 0;

  // For single-box backward compat
  const varietyIds = varietySelections[boxId] || [];

  const handleVarietySelect = (boxIdForVariety, index, optionId) => {
    setVarietySelections(prev => {
      const currentIds = [...(prev[boxIdForVariety] || [])];
      if (optionId === '') {
        currentIds.splice(index, 1);
      } else {
        currentIds[index] = parseInt(optionId, 10);
      }
      return { ...prev, [boxIdForVariety]: currentIds };
    });
  };

  // Get available options for a dropdown (exclude already selected, except current)
  const getAvailableOptions = (boxIdForVariety, currentIndex) => {
    const boxObj = campaign.boxes.find(b => b.id === boxIdForVariety);
    const options = boxObj?.variety_options || [];
    const currentSelections = varietySelections[boxIdForVariety] || [];
    return options.filter(option => {
      const selectedAtOtherIndex = currentSelections.findIndex((id, idx) => id === option.id && idx !== currentIndex);
      return selectedAtOtherIndex === -1;
    });
  };

  /*
   * address
   */

  const [ addressLoading, setAddressLoading ] = useState(true);
  const [ addressInitial, setAddressInitial ] = useState({
    addr_line_1: '',
    addr_line_2: '',
    addr_city: '',
    division: '',
    post_code: '',
    country: '',
  });
  const [ addressVerified, setAddressVerified ] = useState(false);
  const [ verifiedAddress, setVerifiedAddress ] = useState(null);

  useEffect(() => {

    api(token).v1.profile.address.query()
      .then(res => {
        if (res.status === 200) {
          setAddressLoading(false);
          setAddressInitial({
            addr_line_1: res.body.data.addr_line_1,
            addr_line_2: res.body.data.addr_line_2,
            addr_city: res.body.data.addr_city,
            division: res.body.data.division,
            post_code: res.body.data.post_code,
            country: res.body.data.country,
          });
        }
      })
      .catch(err => {
        setAddressLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });

  }, [token]);

  const verifyAddress = async (values) => {
    if (loading) return;

    // Basic validation
    if (!values.addr_line_1 || !values.addr_city || !values.country || !values.division || !values.post_code) {
      toast.show(toast.KIND.ERROR, 'Please fill in all required address fields.');
      return;
    }

    setLoading(true);

    try {
      const res = await api(token).v1.profile.address.verify.post(values);

      if (res.status === 200) {
        if (res.body.is_valid && res.body.address) {
          setVerifiedAddress(res.body.address);
          setAddressVerified(true);
        } else {
          toast.show(toast.KIND.ERROR, 'We could not verify your address. Please check your address and try again.');
        }
      } else {
        toast.show(toast.KIND.ERROR, 'We could not verify your address. Please check your address and try again.');
      }
    } catch (err) {
      toast.show(toast.KIND.ERROR, 'We could not verify your address. Please check your address and try again.');
    } finally {
      setLoading(false);
    }
  };

  const editAddress = () => {
    setAddressVerified(false);
    setVerifiedAddress(null);
  };

  /*
   * signature
   */

  const canvas = useRef();

  /*
   * actions (opt in / opt out)
   */

  const { setDialog } = dialogs.utils.useDialog();

  const optIn = (values) => {

    const _submit = (blob) => {

      const form = new FormData();

      if (data.opt_in.signature && blob) {
        form.append('sign_value', initials);
        form.append('sign_media', blob, `${uuid_v4()}.png`);
      }

      if (data.opt_in.address && verifiedAddress) {
        form.append('addr_line_1', verifiedAddress.addr_line_1);
        form.append('addr_line_2', verifiedAddress.addr_line_2 || '');
        form.append('addr_city', verifiedAddress.addr_city);

        form.append('country', verifiedAddress.country);
        form.append('division', verifiedAddress.division);
        form.append('post_code', verifiedAddress.post_code);
      }

      if (isMultiBox) {
        boxIds.forEach(id => form.append('box_ids', id));

        // Send per-box variety selections as JSON
        const vsWithEntries = Object.fromEntries(
          Object.entries(varietySelections).filter(([, ids]) => ids.length > 0)
        );
        if (Object.keys(vsWithEntries).length > 0) {
          form.append('variety_selections', JSON.stringify(vsWithEntries));
        }
      } else {
        form.append('box_id', boxId);

        if (requiresVariety && varietyIds.length > 0) {
          varietyIds.forEach(id => form.append('variety_ids', id));
        }
      }

      setLoading(true);

      api(token).v1.campaigns.opt_in
        .post(
          { campaign: campaign.id },
          form,
        )
        .then(res => {
          setLoading(false);

          if (res.status === 200) {
            campaign.user.active = true;
            campaign.user.enable = false;

            setDialog();
          }

          if (res.status === 400) {
            toast.show(toast.KIND.ERROR, res.body);
          }
        })
        .catch(err => {
          setLoading(false);
          toast.show(toast.KIND.WARNING, err);
        });
    };

    /*
     * Verify that the number of selected boxes is within the allowed range.
     */
    if (boxIds.length < boxSelectionMin || boxIds.length > boxSelectionMax) {
      let msg;
      if (boxSelectionMin === boxSelectionMax) {
        msg = boxSelectionMin === 1
          ? 'Please select an option.'
          : `Please select ${boxSelectionMin} options.`;
      } else {
        msg = `Please select between ${boxSelectionMin} and ${boxSelectionMax} options.`;
      }
      toast.show(toast.KIND.ERROR, msg);
      return;
    }

    /*
     * Verify that variety selections are complete for each box.
     */
    for (const box of selectedBoxes) {
      if (box.variety_selection_count > 0) {
        const ids = varietySelections[box.id] || [];
        if (ids.length !== box.variety_selection_count) {
          toast.show(toast.KIND.ERROR,
            `Please select ${box.variety_selection_count} different varieties for ${box.title.external}.`
          );
          return;
        }
      }
    }

    /*
     * Retrieve the signature blob.
     */
    if (data.opt_in.signature) {
      if (canvas.current.isEmpty()) {
        toast.show(toast.KIND.ERROR, 'Please provide a signature.');
        return;
      } else {
        canvas.current._canvas.toBlob(
          blob => {
            // Some browsers (notably iOS Safari) can hand back a 0-byte or
            // null Blob even when something was drawn. Posting that triggers a
            // confusing "The submitted file is empty" error from the server, so
            // catch it here and ask the member to sign again.
            if (!blob || blob.size === 0) {
              toast.show(toast.KIND.ERROR, 'We could not capture your signature. Please sign again.');
              return;
            }
            _submit(blob);
          },
          'image/png',
          .9,
        );
      }
    } else {
      _submit();
    }
  };

  const optOut = () => {

    setLoading(true);

    api(token).v1.campaigns.opt_out
      .post({ campaign: campaign.id })
      .then(res => {
        setLoading(false);

        if (res.status === 200) {
          campaign.user.active = false;
          campaign.user.enable = false;

          setDialog();
        }

        if (res.status === 400) {
          toast.show(toast.KIND.ERROR, res.body);
        }
      })
      .catch(err => {
        setLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });
  };

  /*
   * content
   */

  return (
    <Base title='Campaign'>
      <div className={styles.root}>
        {
          loading && (
            <p>Loading...</p>
          )
        }

        {
          data && (
            <Formik
              enableReinitialize={true}
              initialValues={{
                ...addressInitial,
              }}
              validateOnChange={false}
              validationSchema={
                data.opt_in.address
                ? (
                  yup.object({
                    addr_line_1: yup.string()
                      .required('Required'),
                    addr_line_2: yup.string(),
                    addr_city: yup.string()
                      .required('Required'),
                    division: yup.string()
                      .required('Required'),
                    post_code: yup.string()
                      .required('Required'),
                    country: yup.string()
                      .required('Required'),
                  })
                )
                : (
                  yup.object({
                  })
                )
              }
              onSubmit={(values, actions) => optIn(values)} >

              <Form>
                <div className={styles.notice}>
                  To participate in the <i>{campaign.title.external}</i> campaign,
                  please review and type your initials (<span>{initials || '...'}</span>)
                  next to each task below to confirm your agreement.
                </div>

                {data.opt_in.points !== 'no_points' && (
                  <div className={styles.notice}>
                    {data.opt_in.points === 'regular'
                      ? "You'll use one Chimeable Point to join, and once you complete the tasks, you'll get it back for future campaigns."
                      : "If you have Chimeable Points, one will be used to join. You can still participate with zero points."}
                  </div>
                )}

                {
                  /*
                   * TERMS
                   */
                }
                <div className={styles.terms}>
                  <html.Content
                    className={styles.content}
                    aria-label='campaign-terms'
                    html={data.opt_in.tac}
                    context={{
                      initials: initials || '',
                    }}
                  />

                  <label>
                    <input
                      type='checkbox'
                      required={true}
                      checked={termsAccepted}
                      onChange={(event) => {
                        setTermsAccepted(event.target.checked);
                      }}
                    />
                    I have read and accept the <a href="/campaign-terms-conditions" target="_blank" rel="noopener noreferrer">terms and conditions</a> for this campaign.
                  </label>
                </div>

                {
                  /*
                   * BOXES
                   */

                  (campaign.boxes.length > 1) && (

                    <div className={styles.boxes}>
                      <p>
                        {!isMultiBox
                          ? `There are ${campaign.boxes.length} options available. Select one.`
                          : (boxSelectionMin === boxSelectionMax
                              ? `There are ${campaign.boxes.length} options available. Select ${boxSelectionMax}.`
                              : `There are ${campaign.boxes.length} options available. Select between ${boxSelectionMin} and ${boxSelectionMax}.`)
                        }
                      </p>

                      {
                        campaign.boxes
                        .map(box => (
                          <div
                            key={box.id}
                            className={styles.box}
                            role='button'
                            aria-label={box.title.external}
                            onClick={() => handleBoxToggle(box.id)}>

                            <div
                              className={`
                                ${styles.frame}
                                ${boxIds.includes(box.id) ? styles.active : ''}
                              `}>

                              <div className={styles.image}>
                                <LazyLoadImage
                                  src={box.image}
                                  alt={box.title.external}
                                />
                              </div>
                            </div>

                            <div className={styles.label}>
                              {box.title.external}
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  )
                }

                {
                  /*
                   * VARIETIES
                   */

                  requiresVariety && (
                    <div className={styles.varieties}>
                      {boxesWithVarieties.map(box => {
                        const boxVarietyOptions = box.variety_options || [];
                        const boxVarietyInstructions = box.variety_instructions || '';
                        const boxVarietyIds = varietySelections[box.id] || [];
                        const boxVarietyCount = box.variety_selection_count;

                        return (
                          <div key={box.id}>
                            {isMultiBox && (
                              <p className={styles.varietyBoxTitle}>
                                <strong>{box.title.external}</strong>
                              </p>
                            )}

                            {boxVarietyInstructions && (
                              <div
                                className={styles.instructions}
                                dangerouslySetInnerHTML={{ __html: boxVarietyInstructions }}
                              />
                            )}

                            <p>
                              Select {boxVarietyCount} different varieties:
                            </p>

                            <div className={styles.dropdowns}>
                              {
                                Array.from({ length: boxVarietyCount }).map((_, index) => {
                                  const selectedId = boxVarietyIds[index];
                                  const selectedOption = boxVarietyOptions.find(opt => opt.id === selectedId);
                                  const availableOptions = getAvailableOptions(box.id, index);

                                  return (
                                    <VarietyDropdown
                                      key={`${box.id}-${index}`}
                                      index={index}
                                      selectedOption={selectedOption}
                                      availableOptions={availableOptions}
                                      onSelect={(optionId) => handleVarietySelect(box.id, index, optionId)}
                                    />
                                  );
                                })
                              }
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                }

                {
                  /*
                   * ADDRESS
                   */

                  data.opt_in.address && !addressVerified && (
                    <div className={styles.address}>

                      <addr.AddrLine1 loading={addressLoading} />
                      <addr.AddrLine2 loading={addressLoading} />
                      <addr.City loading={addressLoading} />
                      <addr.Division loading={addressLoading} />
                      <addr.PostCode loading={addressLoading} />
                      <addr.Country loading={addressLoading} />

                    </div>
                  )
                }

                {
                  /*
                   * VERIFIED ADDRESS
                   */

                  data.opt_in.address && addressVerified && verifiedAddress && (
                    <div className={styles.verifiedAddress}>
                      <div className={styles.verifiedLabel}>Verified Address</div>
                      <div className={styles.verifiedContent}>
                        <p>{verifiedAddress.addr_line_1}</p>
                        {verifiedAddress.addr_line_2 && <p>{verifiedAddress.addr_line_2}</p>}
                        <p>{verifiedAddress.addr_city}, {verifiedAddress.division} {verifiedAddress.post_code}</p>
                        <p>{verifiedAddress.country}</p>
                      </div>
                      <button
                        type='button'
                        className={styles.editAddressBtn}
                        onClick={editAddress}
                        disabled={loading}>
                        Edit Address
                      </button>
                    </div>
                  )
                }

                {
                  /*
                   * SIGNATURE
                   */

                  data.opt_in.signature && (
                    <div className={styles.signature}>
                      <div className={styles.label}>
                        Signature
                      </div>

                      <div className={styles.frame}>
                        <SignatureCanvas
                          ref={canvas}
                          aria-label='signature'
                          penColor='black'
                          canvasProps={{
                            width: 300,
                            height: 140,
                          }}
                        />
                      </div>

                      <button
                        type='button'
                        onClick={() => canvas.current.clear()}>

                        Clear

                      </button>
                    </div>
                  )
                }

                {
                  /*
                   * ACTIONS
                   */
                }
                <ActionsSection
                  loading={loading}
                  optOut={optOut}
                  requiresAddress={data.opt_in.address}
                  addressVerified={addressVerified}
                  verifyAddress={verifyAddress}
                />
              </Form>
            </Formik>
          )
        }
      </div>
    </Base>
  );
};

export default OptInOut;